import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { SCOPES } from '../api';
import { EmptyState, ErrorBox, Page } from '../components/common';
import type { HubLogin } from '../session';
import { StatusBadge, worstStatus } from '../components/status';
import { IssueList, joinParts, pct, type Issue } from './dashboard/shared';
import { opsIssues, opsNumbers, useHubStatus } from './dashboard/ops';
import { identityIssues, useIdentitySummary } from './dashboard/identity';
import { authUsageIssues, useAuthUsage } from './dashboard/authUsage';
import { adminIssues, useAdminSummary } from './dashboard/admin';
import { statsIssues, useStatsSummary } from './dashboard/stats';
import { buddyIssues, useBuddySummary } from '../buddy/dashboard/summary';
import { compact } from '../buddy/shared';
import { countBy, printQueueIssues, usePrintQueueSummary } from '../printqueue/dashboard/summary';
import { isStuck } from '../printqueue/shared';
import {
  automationIssues,
  connectedCount,
  outdatedAgents,
  useAutomationSummary,
} from '../automation/dashboard/summary';
import { billingIssues, nextSchedule, useBillingSummary } from '../billing/dashboard/summary';
import { productionIssues, productionLines, useProductionSummary } from '../prodstats/dashboard/summary';
import { PRODUCTION_PATH } from '../prodstats/api/prodstats';
import { formatDateTime } from '../lib/format';

/** One group at a glance: worst status, a few numbers, and the way to its dashboard. */
function GroupCard({
  title,
  to,
  status,
  loading,
  lines,
  testId,
}: {
  title: string;
  to: string;
  status: string | null;
  loading: boolean;
  lines: ReactNode[];
  testId: string;
}) {
  return (
    <Link to={to} className="nyk-card nyk-group-card" data-testid={testId} data-status={status ?? 'loading'}>
      <div className="nyk-group-card-head">
        <h2>{title}</h2>
        {status ? (
          <StatusBadge status={status} />
        ) : loading ? (
          <span className="nyk-ui-hint">loading…</span>
        ) : null}
      </div>
      <ul className="nyk-group-card-lines">
        {lines.filter(Boolean).map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
      <span className="nyk-group-card-more">
        Open {title} <ArrowRight size={14} aria-hidden="true" />
      </span>
    </Link>
  );
}

const statusOf = (issues: Issue[]) => worstStatus(issues.map((i) => i.status));

/** Landing page: the state of every group and one list of what needs attention, each linking to its group. */
export function DashboardPage({ login }: { login: HubLogin }) {
  const canOps = hasScope(login.scopes, SCOPES.podsRead) || hasScope(login.scopes, SCOPES.dbRead);
  const isAdmin = hasScope(login.scopes, SCOPES.admin);
  const ops = useHubStatus(canOps);
  const identity = useIdentitySummary();
  const usage = useAuthUsage();
  const admin = useAdminSummary(isAdmin);
  const canStats = hasScope(login.scopes, SCOPES.statsRead);
  const st = useStatsSummary(canStats);
  const buddy = useBuddySummary();
  const pq = usePrintQueueSummary();
  const ag = useAutomationSummary();
  const bl = useBillingSummary();
  const pr = useProductionSummary();

  const opsList = ops.data ? opsIssues(ops.data).map((i) => ({ ...i, group: 'Operations' })) : [];
  const idList = identity.data
    ? [...identityIssues(identity.data), ...authUsageIssues(usage.data ?? [])].map((i) => ({
        ...i,
        group: 'Identity',
      }))
    : [];
  const adminList = admin.data ? adminIssues(admin.data).map((i) => ({ ...i, group: 'Admin' })) : [];
  const statsList = st.data ? statsIssues(st.data).map((i) => ({ ...i, group: 'Statistics' })) : [];
  const buddyList = buddy.data ? buddyIssues(buddy.data).map((i) => ({ ...i, group: 'Buddy' })) : [];
  const pqList = pq.data ? printQueueIssues(pq.data).map((i) => ({ ...i, group: 'Print queue' })) : [];
  const agList = ag.data ? automationIssues(ag.data).map((i) => ({ ...i, group: 'Automation' })) : [];
  const blList = bl.data ? billingIssues(bl.data).map((i) => ({ ...i, group: 'Billing' })) : [];
  const prList = pr.data ? productionIssues(pr.data).map((i) => ({ ...i, group: 'Statistics' })) : [];
  const fleet = st.data?.fleet;
  const n = ops.data ? opsNumbers(ops.data) : null;
  const loading =
    ops.loading ||
    identity.loading ||
    admin.loading ||
    st.loading ||
    buddy.loading ||
    pq.loading ||
    ag.loading ||
    bl.loading ||
    pr.loading;
  const any =
    canOps ||
    canStats ||
    buddy.enabled ||
    pq.enabled ||
    ag.enabled ||
    bl.enabled ||
    pr.enabled ||
    identity.enabled ||
    isAdmin;

  return (
    <Page
      title="Dashboard"
      description="Status and issues across operations, statistics, nyce.buddy, the print queue, the automation gateway, billing, identity and administration. Open a group for the details."
      actions={
        any && (
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={loading}
            onClick={() => {
              void ops.reload();
              identity.reload();
              usage.reload();
              void admin.reload();
              st.reload();
              buddy.reload();
              pq.reload();
              ag.reload();
              void bl.reload();
              void pr.reload();
            }}
          >
            Refresh
          </Button>
        )
      }
    >
      <ErrorBox
        error={
          ops.error ||
          admin.error ||
          identity.error ||
          st.error ||
          buddy.error ||
          pq.error ||
          ag.error ||
          bl.error ||
          pr.error
        }
      />
      {!any && (
        <EmptyState
          card
          title="No status to show"
          hint="Operations, statistics, identity or admin access shows the state here."
        />
      )}
      {any && (
        <>
          <div className="nyk-group-grid" data-testid="group-cards">
            {canOps && (
              <GroupCard
                title="Operations"
                to="/operations"
                testId="group-operations"
                loading={ops.loading}
                status={ops.data ? statusOf(opsList) : null}
                lines={
                  n
                    ? [
                        ops.data?.environments && (
                          <>
                            <b>{n.envOk}</b>/{n.envActive} environments healthy
                            {n.envDown > 0 && <span className="nyk-text-red"> · {n.envDown} down</span>}
                          </>
                        ),
                        ops.data?.environments && (
                          <>
                            <b>{n.podsRunning}</b>/{n.podsTotal} pods running
                            {n.failing > 0 && <span className="nyk-text-red"> · {n.failing} failing</span>}
                          </>
                        ),
                        ops.data?.databases && (
                          <>
                            <b>{n.dbOk}</b>/{n.dbs.length} databases healthy · {pct(n.conns, n.maxConns) ?? 0}{' '}
                            % connections
                          </>
                        ),
                        ops.data?.jobs && (
                          <>
                            <b>{ops.data.jobs.failed}</b> failed jobs in 24 h
                          </>
                        ),
                      ]
                    : []
                }
              />
            )}
            {canStats && (
              <GroupCard
                title="Statistics"
                to="/statistics"
                testId="group-statistics"
                loading={st.loading}
                status={st.data ? statusOf(statsList) : null}
                lines={
                  fleet
                    ? [
                        <>
                          <b>{fleet.installations.ok}</b>/
                          {fleet.installations.ok + fleet.installations.silent} installations reporting
                          {fleet.installations.silent > 0 && (
                            <span className="nyk-text-red"> · {fleet.installations.silent} silent</span>
                          )}
                        </>,
                        <>
                          <b>{fleet.incidentsOpen}</b> open incidents · <b>{fleet.errorsOpen}</b> open errors
                        </>,
                        st.data?.ingest && (
                          <>
                            Processor{' '}
                            {st.data.ingest.processor.stale ? (
                              <span className="nyk-text-red">not running</span>
                            ) : (
                              'running'
                            )}{' '}
                            · backlog {fleet.backlogRows.toLocaleString()}
                          </>
                        ),
                      ]
                    : []
                }
              />
            )}
            {pr.enabled && (
              <GroupCard
                title="Production"
                to={PRODUCTION_PATH}
                testId="group-production"
                loading={pr.loading}
                status={pr.data ? statusOf(prList) : null}
                lines={pr.data ? productionLines(pr.data) : []}
              />
            )}
            {buddy.enabled && (
              <GroupCard
                title="Buddy"
                to="/buddy"
                testId="group-buddy"
                loading={buddy.loading}
                status={buddy.data ? statusOf(buddyList) : null}
                lines={(buddy.data ?? []).map((b) => (
                  <>
                    <b>{b.target.label}</b>{' '}
                    {!b.signedIn ? (
                      <Chip size="sm">signed out</Chip>
                    ) : b.usage ? (
                      joinParts([
                        `${b.usage.totals.questions.toLocaleString()} questions`,
                        `${compact(b.usage.totals.weightedTokens)} NYCE tokens in 30 d`,
                        !!b.feedback?.unhandled && `${b.feedback.unhandled} feedback to triage`,
                      ])
                    ) : (
                      <span className="nyk-text-red">unreadable</span>
                    )}
                  </>
                ))}
              />
            )}
            {pq.enabled && (
              <GroupCard
                title="Print queue"
                to="/printqueue"
                testId="group-printqueue"
                loading={pq.loading}
                status={pq.data ? statusOf(pqList) : null}
                lines={(pq.data ?? []).map((p) => {
                  const c = countBy(p.open);
                  const stuck = p.open.filter((j) => isStuck(j)).length;
                  return (
                    <>
                      <b>{p.target.label}</b>{' '}
                      {p.error ? (
                        <span className="nyk-text-red">unreadable</span>
                      ) : (
                        joinParts([
                          `${c.Registered + c.Fetched + c.Printing} unfinished`,
                          !!stuck && `${stuck} stuck`,
                          !!c.Error && `${c.Error} failed`,
                          p.printers && `${p.printers.length} printers`,
                        ])
                      )}
                    </>
                  );
                })}
              />
            )}
            {ag.enabled && (
              <GroupCard
                title="Automation"
                to="/automation"
                testId="group-automation"
                loading={ag.loading}
                status={ag.data ? statusOf(agList) : null}
                lines={(ag.data ?? []).map((a) => {
                  const offline = a.agents.length - connectedCount(a);
                  const old = outdatedAgents(a).length;
                  return (
                    <>
                      <b>{a.target.label}</b>{' '}
                      {a.error ? (
                        <span className="nyk-text-red">unreadable</span>
                      ) : (
                        joinParts([
                          `${connectedCount(a)}/${a.agents.length} agents connected`,
                          !!offline && `${offline} offline`,
                          !!old && `${old} to update`,
                        ])
                      )}
                    </>
                  );
                })}
              />
            )}
            {bl.enabled && (
              <GroupCard
                title="Billing"
                to="/billing"
                testId="group-billing"
                loading={bl.loading}
                status={bl.data ? statusOf(blList) : null}
                lines={
                  bl.data
                    ? [
                        <>
                          <b>{bl.data.schedules.filter((x) => x.enabled).length}</b> schedules
                          {nextSchedule(bl.data) &&
                            ` · next ${formatDateTime(nextSchedule(bl.data)!.retryAt ?? nextSchedule(bl.data)!.nextRunAt)}`}
                        </>,
                      ]
                    : []
                }
              />
            )}
            {identity.enabled && (
              <GroupCard
                title="Identity"
                to="/identity"
                testId="group-identity"
                loading={identity.loading}
                status={identity.data ? statusOf(idList) : null}
                lines={(identity.data ?? []).map((t) => (
                  <>
                    <b>{t.target.label}</b>{' '}
                    {t.signedIn ? (
                      joinParts([
                        t.tenants && `${t.tenants.active} active tenants`,
                        t.scim && `${t.scim.total} SCIM endpoints`,
                        !!t.error && 'error',
                      ])
                    ) : (
                      <Chip size="sm">signed out</Chip>
                    )}
                  </>
                ))}
              />
            )}
            {isAdmin && (
              <GroupCard
                title="Admin"
                to="/admin"
                testId="group-admin"
                loading={admin.loading}
                status={admin.data ? statusOf(adminList) : null}
                lines={
                  admin.data
                    ? [
                        <>
                          <b>{admin.data.clusters.total}</b> clusters ·{' '}
                          <b>{admin.data.environments.registered}</b> registered environments
                        </>,
                        <>
                          <b>{admin.data.audit.writes}</b> writes in 24 h
                          {admin.data.audit.errors.length + admin.data.audit.denied.length > 0 && (
                            <span className="nyk-text-amber">
                              {' '}
                              · {admin.data.audit.errors.length} failed, {admin.data.audit.denied.length}{' '}
                              denied
                            </span>
                          )}
                        </>,
                        admin.data.withoutCredentials > 0 && (
                          <>
                            <b>{admin.data.withoutCredentials}</b> DB instances without credentials
                          </>
                        ),
                      ]
                    : []
                }
              />
            )}
          </div>
          <IssueList
            issues={[
              ...opsList,
              ...statsList,
              ...buddyList,
              ...pqList,
              ...agList,
              ...blList,
              ...prList,
              ...idList,
              ...adminList,
            ]}
            max={10}
          />
        </>
      )}
      <Me login={login} />
    </Page>
  );
}

function Me({ login }: { login: HubLogin }) {
  return (
    <details className="nyk-card oph-dash-me">
      <summary>
        Signed in as <b>{login.username || login.sub}</b>
        {login.tenantId && <span className="nyk-ui-hint"> · tenant {login.tenantId}</span>}
      </summary>
      <div className="oph-dash-scopes" data-testid="scopes">
        {login.scopes.length ? (
          login.scopes.map((s) => (
            <Chip key={s} size="sm">
              {s}
            </Chip>
          ))
        ) : (
          <span className="nyk-ui-hint">No hub scopes.</span>
        )}
      </div>
    </details>
  );
}
